// src/components/pages/OpenGameroom.js
import React, { useState, useRef, useMemo, useLayoutEffect } from "react";
import {
  Box,
  Chip,
  Paper,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Button,
  IconButton,
} from "@mui/material";
import {
  Menu as MenuIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  HourglassEmpty as HourglassEmptyIcon,
  EmojiEvents as EmojiEventsIcon,
  PlayArrow as PlayArrowIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import _ from "underscore";
import AuthService from "../../utils/AuthService";
import GameServer from "./GameServer"; // ✅ simple per-game WebSocket
import GameCode from "./GameCode";
import GameStartLink from "./GameStartLink";

function OpenGameroom({ setShowAppBar }) {
  const { contentId, courseId, chapterId, moduleId } = useParams();
  const [gameCode, setGameCode] = useState(null);
  const [players, setPlayers] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const tmpPlayers = useRef([]);
  const navigate = useNavigate();

  // 🔹 Hide AppBar for immersive view
  useLayoutEffect(() => {
    setShowAppBar?.(false);
    return () => setShowAppBar?.(true);
  }, [setShowAppBar]);

  // --------------------------
  // Helper utilities
  // --------------------------
  const normalizeToArray = (v) => (!v ? [] : Array.isArray(v) ? v : [v]);

  const extractWaitingRoomUsers = (raw) => {
    if (!raw) return [];
    const payloads = [raw, raw.message, raw.Message].filter(Boolean);
    for (const p of payloads) {
      let obj = p;
      if (typeof p === "string") {
        try {
          obj = JSON.parse(p);
        } catch {
          continue;
        }
      }
      const list = obj?.usersInWaitingRoom ?? obj?.UsersInWaitingRoom;
      if (list) return normalizeToArray(list);
    }
    return [];
  };

  // --------------------------
  // Initialize game code
  // --------------------------
  const initGameCode = (code, contentType) => {
    console.log("🎮 Initializing Game:", code, contentType);
    setGameCode(code);

    // 🔹 Create per-game WebSocket channel
    GameServer.notify(
      code,
      "startgame",
      { start: "game..." },
      {
        joined: (msg) => {
          const waiting = extractWaitingRoomUsers(msg);
          if (waiting?.length) {
            console.log("🧑‍🎓 Joined users:", waiting);
            setPlayers(waiting);
          }
        },
        gameon: (msg) => {
          console.log("✅ Game started:", msg);
          setGameStarted(true);
          navigate(`/WatchGame/${msg.game || code}`);
        },
      }
    );
  };

  // --------------------------
  // Instructor starts the game
  // --------------------------
  const handleStartGame = () => {
    if (!gameCode) return;
    GameServer.notify(
      gameCode,
      "launchgame",
      { launch: "game..." },
      {
        gameon: (msg) => {
          console.log("🎬 Game launched:", msg);
          setGameStarted(true);
          navigate(`/WatchGame/${msg.game || gameCode}`);
        },
      }
    );
  };

  // --------------------------
  // Merge players uniquely
  // --------------------------
  const allPlayers = useMemo(() => {
    const incoming = players ? normalizeToArray(players) : [];
    const combined = tmpPlayers.current.concat(incoming);
    const unique = _.uniq(combined, (x) => x.UserId || x.userId || x.Id);
    tmpPlayers.current = unique;
    return unique;
  }, [players]);

  const normalizeStatus = (v) => {
    if (!v) return "waiting";
    const n = v.toString().toLowerCase().trim();
    if (["done", "completed", "finished", "submitted"].includes(n))
      return "completed";
    if (["active", "playing", "inprogress", "live"].includes(n))
      return "active";
    return "waiting";
  };

  const summary = useMemo(() => {
    const s = { total: allPlayers.length, active: 0, waiting: 0, completed: 0 };
    allPlayers.forEach((p) => {
      s[normalizeStatus(p.Status || p.status || p.State)]++;
    });
    return s;
  }, [allPlayers]);

  const formatDate = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    return isNaN(d) ? "—" : d.toLocaleString();
  };

  const participants = allPlayers.map((p, i) => {
    const first = p.FirstName || p.firstName || "Player";
    const last = p.LastName || p.lastName || "";
    const name = `${first} ${last}`.trim();
    const initials = (name.match(/\b\w/g) || [])
      .slice(0, 2)
      .join("")
      .toUpperCase();
    const joined = p.JoinedOn || p.joinedOn || p.CreatedOn;
    const lastSeen = p.LastSeen || p.lastSeen || joined;
    return {
      id: p.UserId || p.userId || p.Id || i,
      name,
      initials,
      team: p.TeamName || p.teamName || "—",
      email: p.Email || p.email || "",
      joinedLabel: formatDate(joined),
      lastLabel: formatDate(lastSeen),
    };
  });

  const sorted = participants.sort((a, b) => a.name.localeCompare(b.name));
  const sessionState = gameStarted
    ? "LIVE"
    : allPlayers.length
    ? "READY"
    : "IDLE";

  // --------------------------
  // Render
  // --------------------------
  return (
    <Box sx={{ bgcolor: "#f4f6fb", minHeight: "100vh", pb: 6 }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, sm: 4 } }}>
        <Button
          color="primary"
          onClick={() => navigate("/coursepage")}
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            color: "primary.main",
            textDecoration: "underline",
            textDecorationColor: "#424242",
          }}
        >
          My Courses
        </Button>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 4,
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton disabled>
              <MenuIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                Live Quiz Moderator
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monitor participation and launch your quiz session.
              </Typography>
            </Box>
            <Chip
              label={`State: ${sessionState}`}
              sx={{ bgcolor: "#e2e8f0", fontWeight: 600 }}
            />
          </Box>

          {/* Game Code + Start */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5, // slightly smaller spacing
            }}
          >
            <GameCode
              contentid={contentId}
              courseid={courseId}
              chapterid={chapterId}
              moduleid={moduleId}
              setGamecode={initGameCode}
            >
              {({ gameCode: code, isLoading }) => (
                <Chip
                  label={isLoading ? "Generating PIN..." : `Game PIN: ${code}`}
                  sx={{
                    bgcolor: "#dbeafe",
                    color: "#1d4ed8",
                    fontWeight: 600,
                    height: 28,
                    fontSize: "0.8rem",
                  }}
                />
              )}
            </GameCode>

            {!gameStarted ? (
              allPlayers.length && gameCode ? (
                <GameStartLink
                  gameCode={gameCode}
                  onGameStart={handleStartGame}
                  label="START QUIZ GAME"
                  startIcon={<PlayArrowIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    borderRadius: 20,
                    px: 2,
                    py: 0.5,
                    fontSize: "0.8rem",
                    minHeight: "32px",
                    textTransform: "none",
                  }}
                />
              ) : (
                <Button
                  variant="contained"
                  disabled
                  startIcon={<PlayArrowIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    borderRadius: 20,
                    px: 2,
                    py: 0.5,
                    fontSize: "0.8rem",
                    minHeight: "32px",
                    textTransform: "none",
                  }}
                >
                  Waiting for players
                </Button>
              )
            ) : (
              <Chip
                label="Game in Progress"
                sx={{
                  bgcolor: "#bbf7d0",
                  color: "#166534",
                  fontWeight: 600,
                  height: 28,
                  fontSize: "0.8rem",
                }}
              />
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/WatchGame/${gameCode}`)}
              sx={{
                borderRadius: 20,
                px: 2,
                py: 0.5,
                fontSize: "0.8rem",
                minHeight: "32px",
                textTransform: "none",
              }}
            >
              JOIN THE GAME
            </Button>
          </Box>
        </Box>

        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              label: "Total Students",
              value: summary.total,
              icon: InfoOutlinedIcon,
              color: "#2563eb",
              bg: "#dbeafe",
            },
            {
              label: "Active",
              value: summary.active,
              icon: CheckCircleOutlineIcon,
              color: "#16a34a",
              bg: "#dcfce7",
            },
            {
              label: "Waiting",
              value: summary.waiting,
              icon: HourglassEmptyIcon,
              color: "#f59e0b",
              bg: "#fef3c7",
            },
            {
              label: "Completed",
              value: summary.completed,
              icon: EmojiEventsIcon,
              color: "#7c3aed",
              bg: "#ede9fe",
            },
          ].map((s) => (
            <Grid item xs={12} sm={6} md={3} key={s.label}>
              <Paper
                sx={{
                  p: 3,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 3,
                  boxShadow: "0 6px 20px rgba(15,23,42,0.08)",
                }}
              >
                <Box
                  sx={{
                    bgcolor: s.bg,
                    color: s.color,
                    borderRadius: "50%",
                    p: 1.5,
                    mr: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <s.icon />
                </Box>
                <Box>
                  <Typography fontWeight={700} fontSize="1.4rem">
                    {s.value}
                  </Typography>
                  <Typography color="text.secondary">{s.label}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Participants */}
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: "0 8px 30px rgba(15,23,42,0.08)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              p: 3,
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Participants
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Real-time roster of learners in this game lobby.
              </Typography>
            </Box>
            <Chip
              label={`${summary.total} joined • ${summary.active} active`}
              variant="outlined"
              color="primary"
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Last Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.length ? (
                  sorted.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar>{p.initials}</Avatar>
                          <Box ml={2}>
                            <Typography fontWeight={600}>{p.name}</Typography>
                            {p.email && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {p.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{p.team}</TableCell>
                      <TableCell>{p.joinedLabel}</TableCell>
                      <TableCell>{p.lastLabel}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{ py: 4, color: "#94a3b8" }}
                    >
                      Waiting for participants... Share the game PIN to invite
                      students.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
}

export default OpenGameroom;
